import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  LayoutDashboard, Package, ShoppingBag, Bell, Settings, LogOut, RefreshCw,
  Plus, Download, IndianRupee, Users, Lock, X, Loader2, BadgeCheck, Clock3,
  FileDown, ExternalLink, Upload, Trash2, CalendarCheck, MessageSquare, Video, MailPlus,
  GraduationCap, Pencil, Eye, EyeOff, ImagePlus, RotateCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "courses", label: "Courses", icon: GraduationCap },
  { id: "orders", label: "Orders", icon: ShoppingBag },
  { id: "consultations", label: "Consultations", icon: CalendarCheck },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "settings", label: "Settings", icon: Settings },
];

const PILL = {
  paid: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  created: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  pending: "bg-amber-500/10 border-amber-500/30 text-amber-400",
  failed: "bg-red-500/10 border-red-500/30 text-red-400",
  cancelled: "bg-red-500/10 border-red-500/30 text-red-400",
  confirmed: "bg-sky-500/10 border-sky-500/30 text-sky-400",
  completed: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
  new: "bg-sky-500/10 border-sky-500/30 text-sky-400",
  contacted: "bg-[#C9A227]/10 border-[#C9A227]/30 text-[#C9A227]",
  closed: "bg-white/5 border-white/15 text-white/50",
};

const inr = (paise) => `₹${Math.round((paise || 0) / 100).toLocaleString("en-IN")}`;
const dt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const Pill = ({ s }) => (
  <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border ${PILL[s] || "bg-white/5 border-white/15 text-white/60"}`}>
    {s === "paid" || s === "completed" ? <BadgeCheck size={11} /> : s === "created" || s === "pending" ? <Clock3 size={11} /> : null}
    {s}
  </span>
);

const Card = ({ title, sub, action, children, testid }) => (
  <div className="bg-[#111111] border border-white/10 rounded-2xl overflow-hidden" data-testid={testid}>
    {(title || action) && (
      <div className="flex items-center justify-between gap-4 px-6 py-5 border-b border-white/10">
        <div>
          <h3 className="font-display font-bold text-[17px] text-white">{title}</h3>
          {sub && <p className="mt-0.5 text-xs text-white/40">{sub}</p>}
        </div>
        {action}
      </div>
    )}
    {children}
  </div>
);

const StatCard = ({ icon: Icon, label, value, gold, testid }) => (
  <div
    className={`rounded-2xl border p-6 ${gold ? "bg-gradient-to-br from-[#C9A227]/15 to-transparent border-[#C9A227]/30" : "bg-[#111111] border-white/10"}`}
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${gold ? "bg-[#C9A227]/20 text-[#C9A227]" : "bg-white/5 text-white/60"}`}>
        <Icon size={18} strokeWidth={1.8} />
      </span>
    </div>
    <p className={`mt-5 font-display font-black text-[32px] leading-none ${gold ? "text-[#C9A227]" : "text-white"}`}>{value}</p>
    <p className="mt-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white/45">{label}</p>
  </div>
);

const Th = ({ children, right }) => (
  <th className={`px-5 py-3.5 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>
);

const Td = ({ children, right, mono }) => (
  <td className={`px-5 py-4 text-sm text-white/80 ${right ? "text-right" : ""} ${mono ? "font-mono text-xs" : ""}`}>{children}</td>
);

const ActionBtn = ({ children, onClick, gold, testid, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    data-testid={testid}
    className={`inline-flex items-center gap-2 font-mono text-[11px] font-bold tracking-[0.15em] uppercase px-5 py-3 rounded-xl transition-all duration-200 disabled:opacity-50 ${
      gold
        ? "bg-[#C9A227] text-[#0a0a0a] hover:bg-[#E0B82E]"
        : "bg-white/5 border border-white/15 text-white/80 hover:bg-white/10 hover:border-white/25"
    }`}
  >
    {children}
  </button>
);

const inputDark =
  "w-full bg-[#0a0a0a] border border-white/15 focus:border-[#C9A227] outline-none rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/30 transition-colors";

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [view, setView] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [sales, setSales] = useState([]);
  const [workshopSales, setWorkshopSales] = useState([]);
  const [productFiles, setProductFiles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null);
  // blast
  const [blastRes, setBlastRes] = useState("all");
  const [blastSubject, setBlastSubject] = useState("");
  const [blastMessage, setBlastMessage] = useState("");
  const [blastSending, setBlastSending] = useState(false);
  // add product modal
  const [addOpen, setAddOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null); // product id being edited
  const [newProduct, setNewProduct] = useState({ name: "", price: "", description: "", category: "digital", stock: "", download_link: "", status: "coming_soon", active: true });
  const [adding, setAdding] = useState(false);
  const [thumbFor, setThumbFor] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // product pending delete confirmation
  const [deleting, setDeleting] = useState(false);
  const [resetCourseTarget, setResetCourseTarget] = useState(null); // course pending reservation reset
  const [resetting, setResetting] = useState(false);
  // thumbnail picked inside add/edit modal (shared — only one modal open at a time)
  const [pendingThumb, setPendingThumb] = useState(null);
  const [pendingThumbPreview, setPendingThumbPreview] = useState(null);
  // courses
  const [courses, setCourses] = useState([]);
  const [courseOpen, setCourseOpen] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [newCourse, setNewCourse] = useState({ name: "", description: "", price: "", course_type: "", seat_limit: "", status: "coming_soon", launch_date: "", enrollment_link: "" });
  const [savingCourse, setSavingCourse] = useState(false);
  // consultations
  const [consultations, setConsultations] = useState([]);
  const [courseSales, setCourseSales] = useState([]);
  // course enrollments modal
  const [enrollFor, setEnrollFor] = useState(null);
  const [enrollData, setEnrollData] = useState(null);
  const [enrollLoading, setEnrollLoading] = useState(false);
  // download analytics
  const [dlAnalytics, setDlAnalytics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [resendingFor, setResendingFor] = useState(null);

  const headers = { "X-Admin-Token": token };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, e, l, so, pf, ws, cat, crs, con, an, co, da] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
        axios.get(`${API}/shoot-enquiries`, { headers }),
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/product-orders`, { headers }),
        axios.get(`${API}/product-files`, { headers }),
        axios.get(`${API}/workshop-bookings`, { headers }),
        axios.get(`${API}/admin/products`, { headers }),
        axios.get(`${API}/admin/courses`, { headers }),
        axios.get(`${API}/consultations`, { headers }),
        axios.get(`${API}/admin/sales-analytics`, { headers }),
        axios.get(`${API}/course-orders`, { headers }),
        axios.get(`${API}/admin/download-analytics`, { headers }),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setEnquiries(e.data);
      setLeads(l.data);
      setSales(so.data);
      setProductFiles(pf.data);
      setWorkshopSales(ws.data);
      setCatalog(cat.data);
      setCourses(crs.data);
      setConsultations(con.data);
      setAnalytics(an.data);
      setCourseSales(co.data);
      setDlAnalytics(da.data);
      setAuthed(true);
    } catch {
      setAuthed(false);
      toast.error("Galat token ya server error");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) load();
  }, [load, token]);

  const doLogin = () => {
    if (token.trim().length < 4) {
      toast.error("Admin token daalo");
      return;
    }
    localStorage.setItem("admin_token", token.trim());
    load();
  };

  const doLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setAuthed(false);
  };

  const updateStatus = async (type, id, status) => {
    const url = type === "bookings" ? `${API}/bookings/${id}/status` : type === "leads" ? `${API}/leads/${id}/status` : `${API}/shoot-enquiries/${id}/status`;
    try {
      await axios.patch(url, { status }, { headers });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const sendBlast = async () => {
    if (blastSubject.trim().length < 3 || blastMessage.trim().length < 5) {
      toast.error("Subject aur message bharo");
      return;
    }
    setBlastSending(true);
    try {
      const { data } = await axios.post(`${API}/leads/blast`, { resource: blastRes, subject: blastSubject, message: blastMessage }, { headers });
      toast.success(`Blast done — ${data.sent}/${data.recipients} emails bheje${data.failed ? ` (${data.failed} failed)` : ""}`);
      setBlastMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blast failed");
    } finally {
      setBlastSending(false);
    }
  };

  const uploadProductFile = async (productId, file) => {
    if (!file) return;
    setUploadingFor(productId);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/product-files/${productId}`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("File upload ho gayi — instant delivery live");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFor(null);
    }
  };

  const removeProductFile = async (productId) => {
    try {
      await axios.delete(`${API}/product-files/${productId}`, { headers });
      toast.success("File hata di gayi");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleProductStatus = async (productId, current) => {
    const next = current === "live" ? "coming_soon" : "live";
    try {
      await axios.post(`${API}/admin/products/${productId}/status`, { status: next }, { headers });
      toast.success(next === "live" ? "Product LIVE — Buy Now dikhega" : "Product Coming Soon ho gaya");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status change failed");
    }
  };

  const resetPendingThumb = (existingUrl = null) => {
    setPendingThumb(null);
    setPendingThumbPreview((old) => {
      if (old && old.startsWith("blob:")) URL.revokeObjectURL(old);
      return existingUrl;
    });
  };

  const pickThumb = (file) => {
    if (!file) return;
    setPendingThumb(file);
    setPendingThumbPreview((old) => {
      if (old && old.startsWith("blob:")) URL.revokeObjectURL(old);
      return URL.createObjectURL(file);
    });
  };

  const openProductModal = (p = null) => {
    setEditProduct(p ? p.id : null);
    resetPendingThumb(p?.thumbnail_url || null);
    setNewProduct(
      p
        ? { name: p.name, price: String(p.price), description: p.description || "", category: p.category || "digital", stock: p.stock ?? "", download_link: p.download_link || "", status: p.status || "coming_soon", active: p.active !== false, eb_limit: p.early_bird ? String(p.early_bird.limit) : "", eb_price: p.early_bird ? String(p.early_bird.early_price) : "" }
        : { name: "", price: "", description: "", category: "digital", stock: "", download_link: "", status: "coming_soon", active: true, eb_limit: "", eb_price: "" }
    );
    setAddOpen(true);
  };

  const saveProduct = async () => {
    if (newProduct.name.trim().length < 3 || !Number(newProduct.price)) {
      toast.error("Naam aur sahi price daalo");
      return;
    }
    setAdding(true);
    const ebOn = newProduct.eb_limit !== "" || newProduct.eb_price !== "";
    const payload = {
      name: newProduct.name.trim(),
      price: Number(newProduct.price),
      description: newProduct.description.trim(),
      category: newProduct.category,
      stock: newProduct.category === "physical" && newProduct.stock !== "" ? Number(newProduct.stock) : null,
      download_link: newProduct.download_link.trim(),
      status: newProduct.status,
      active: newProduct.active,
      early_bird_limit: ebOn && newProduct.eb_limit !== "" ? Number(newProduct.eb_limit) : null,
      early_bird_price: ebOn && newProduct.eb_price !== "" ? Number(newProduct.eb_price) : null,
    };
    try {
      let pid = editProduct;
      if (editProduct) {
        await axios.put(`${API}/admin/products/${editProduct}`, payload, { headers });
        toast.success("Product update ho gaya");
      } else {
        const res = await axios.post(`${API}/admin/products`, payload, { headers });
        pid = res.data?.product?.id;
        toast.success("Product add ho gaya — store par live hai");
      }
      if (pendingThumb && pid) {
        try {
          const fd = new FormData();
          fd.append("file", pendingThumb);
          await axios.post(`${API}/admin/products/${pid}/thumbnail`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
          toast.success("Thumbnail upload ho gaya");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Product save ho gaya, thumbnail upload fail — table se retry karo");
        }
      }
      resetPendingThumb();
      setAddOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setAdding(false);
    }
  };

  const deleteProduct = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/products/${deleteTarget.id}`, { headers });
      toast.success(`"${deleteTarget.name}" permanently delete ho gaya`);
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const uploadThumb = async (kind, id, file) => {
    if (!file) return;
    setThumbFor(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/admin/${kind}/${id}/thumbnail`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Thumbnail upload ho gaya");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setThumbFor(null);
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/products/${p.id}`, {
        name: p.name, price: p.price, description: p.description || "", category: p.category || "digital",
        stock: p.stock, download_link: p.download_link || "", status: p.status || "coming_soon", active: !p.active,
        early_bird_limit: p.early_bird ? p.early_bird.limit : null,
        early_bird_price: p.early_bird ? p.early_bird.early_price : null,
      }, { headers });
      toast.success(p.active ? "Product hidden — store se hat gaya" : "Product active — store par dikhega");
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const openCourseModal = (c = null) => {
    setEditCourse(c ? c.id : null);
    resetPendingThumb(c?.thumbnail_url || null);
    setNewCourse(
      c
        ? { name: c.name, description: c.description || "", price: String(c.price || ""), course_type: c.course_type || "", seat_limit: c.seat_limit ? String(c.seat_limit) : "", status: c.status, launch_date: c.launch_date || "", enrollment_link: c.enrollment_link || "" }
        : { name: "", description: "", price: "", course_type: "", seat_limit: "", status: "coming_soon", launch_date: "", enrollment_link: "" }
    );
    setCourseOpen(true);
  };

  const saveCourse = async () => {
    if (newCourse.name.trim().length < 3) {
      toast.error("Course name daalo");
      return;
    }
    setSavingCourse(true);
    const payload = {
      name: newCourse.name.trim(),
      description: newCourse.description.trim(),
      price: Number(newCourse.price) || 0,
      course_type: newCourse.course_type.trim(),
      seat_limit: newCourse.seat_limit !== "" ? Number(newCourse.seat_limit) : null,
      status: newCourse.status,
      launch_date: newCourse.launch_date,
      enrollment_link: newCourse.enrollment_link.trim(),
    };
    try {
      let cid = editCourse;
      if (editCourse) {
        await axios.put(`${API}/admin/courses/${editCourse}`, payload, { headers });
        toast.success("Course update ho gaya");
      } else {
        const res = await axios.post(`${API}/admin/courses`, payload, { headers });
        cid = res.data?.course?.id;
        toast.success("Course add ho gaya");
      }
      if (pendingThumb && cid) {
        try {
          const fd = new FormData();
          fd.append("file", pendingThumb);
          await axios.post(`${API}/admin/courses/${cid}/thumbnail`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
          toast.success("Thumbnail upload ho gaya");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Course save ho gaya, thumbnail upload fail — table se retry karo");
        }
      }
      resetPendingThumb();
      setCourseOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingCourse(false);
    }
  };

  const deleteCourse = async (id, name) => {
    if (!window.confirm(`"${name}" course delete kar do?`)) return;
    try {
      await axios.delete(`${API}/admin/courses/${id}`, { headers });
      toast.success("Course delete ho gaya");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggleCourseStatus = async (id, current) => {
    const next = current === "active" ? "coming_soon" : current === "coming_soon" ? "closed" : "active";
    try {
      await axios.post(`${API}/admin/courses/${id}/status`, { status: next }, { headers });
      toast.success(`Course status: ${next.replace("_", " ")}`);
      load();
    } catch {
      toast.error("Status change failed");
    }
  };

  const setConsultStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/consultations/${id}/status`, { status }, { headers });
      toast.success("Status updated");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const resetReservations = async () => {
    if (!resetCourseTarget) return;
    setResetting(true);
    try {
      const { data } = await axios.post(`${API}/admin/courses/${resetCourseTarget.id}/reset-reservations`, {}, { headers });
      toast.success(`"${resetCourseTarget.name}" ke ${data.deleted} reservations clear ho gaye`);
      setResetCourseTarget(null);
      load();
    } catch {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const openEnrollments = async (courseId) => {    setEnrollFor(courseId);
    setEnrollData(null);
    setEnrollLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/courses/${courseId}/enrollments`, { headers });
      setEnrollData(data);
    } catch {
      toast.error("Enrollments load nahi hue");
      setEnrollFor(null);
    } finally {
      setEnrollLoading(false);
    }
  };

  const resendLink = async (purchaseId) => {    setResendingFor(purchaseId);
    try {
      await axios.post(`${API}/admin/product-orders/${purchaseId}/resend`, {}, { headers });
      toast.success("Naya download link customer ke email par bhej diya");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resend failed");
    } finally {
      setResendingFor(null);
    }
  };

  const sendAbandonedReminders = async () => {    setReminding(true);
    try {
      const { data } = await axios.post(`${API}/admin/abandoned-remind`, {}, { headers });
      toast.success(data.sent > 0 ? `${data.sent} reminder email bhej di gayi` : "Koi pending abandoned order nahi (1 ghante se purane)");
      load();
    } catch {
      toast.error("Reminder bhejne mein dikkat aayi");
    } finally {
      setReminding(false);
    }
  };

  const exportCsv = () => {    const dl = (name, rows) => {
      const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
    };
    dl("orders.csv", [["Order ID", "Customer", "Email", "Product", "Amount INR", "Status", "Date"], ...sales.map((s) => [s.purchase_id, s.name, s.email, s.product_name, Math.round((s.amount || 0) / 100), s.status, dt(s.created_at)])]);
    dl("subscribers.csv", [["Name", "Email", "List", "Status", "Date"], ...leads.map((l) => [l.name, l.email, l.resource, l.status, dt(l.created_at)])]);
    toast.success("CSV files download ho rahi hain");
  };

  const leadResources = Array.from(new Set(["all", "ebook-field-guide-2026", ...leads.map((l) => l.resource).filter(Boolean)]));
  const blastCount = blastRes === "all" ? new Set(leads.map((l) => l.email)).size : new Set(leads.filter((l) => l.resource === blastRes).map((l) => l.email)).size;

  /* ---------- LOGIN ---------- */
  if (!authed) {
    return (
      <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-5" data-testid="admin-login">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm bg-[#111111] border border-white/10 rounded-2xl p-9">
          <img src="/assets/logo-white.png" alt="FilmmakerAkki" className="h-12 w-auto" />
          <p className="mt-6 font-mono text-[11px] tracking-[0.3em] uppercase text-[#C9A227]">Admin Console</p>
          <h1 className="mt-2 font-display font-black text-2xl text-white">Dashboard Login</h1>
          <input
            type="password"
            data-testid="admin-token-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doLogin()}
            placeholder="Admin token"
            className={`${inputDark} mt-7`}
          />
          <button
            data-testid="admin-login-button"
            onClick={doLogin}
            disabled={loading}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] text-[#0a0a0a] font-mono text-[11px] font-bold tracking-[0.2em] uppercase px-6 py-4 rounded-xl hover:bg-[#E0B82E] transition-colors disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />} Unlock Dashboard
          </button>
        </motion.div>
      </main>
    );
  }

  /* ---------- DASHBOARD ---------- */
  return (
    <main className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-white/10 bg-[#0d0d0d] sticky top-0 h-screen" data-testid="admin-sidebar">
        <div className="px-6 py-7 border-b border-white/10">
          <img src="/assets/logo-white.png" alt="FilmmakerAkki" className="h-11 w-auto" />
          <p className="mt-3 font-mono text-[9px] tracking-[0.3em] uppercase text-[#C9A227]">Admin Console</p>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = view === n.id;
            return (
              <button
                key={n.id}
                data-testid={`admin-nav-${n.id}`}
                onClick={() => setView(n.id)}
                className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-xl font-mono text-[12px] font-bold tracking-[0.12em] uppercase transition-all duration-200 ${
                  active ? "bg-[#C9A227]/15 text-[#C9A227] border border-[#C9A227]/30" : "text-white/55 hover:text-white hover:bg-white/5 border border-transparent"
                }`}
              >
                <Icon size={17} strokeWidth={1.8} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-5 border-t border-white/10 space-y-1.5">
          <button onClick={load} data-testid="admin-refresh" className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl font-mono text-[11px] font-bold tracking-[0.12em] uppercase text-white/55 hover:text-white hover:bg-white/5 transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3.5 px-4 py-3 rounded-xl font-mono text-[11px] font-bold tracking-[0.12em] uppercase text-red-400/70 hover:text-red-400 hover:bg-red-500/10 transition-colors">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* mobile nav */}
        <div className="md:hidden sticky top-0 z-40 bg-[#0d0d0d] border-b border-white/10 px-4 py-3 flex items-center gap-2 overflow-x-auto">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.id}
                data-testid={`admin-nav-mobile-${n.id}`}
                onClick={() => setView(n.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-mono text-[10px] font-bold tracking-[0.12em] uppercase whitespace-nowrap ${view === n.id ? "bg-[#C9A227]/15 text-[#C9A227] border border-[#C9A227]/30" : "text-white/55 border border-white/10"}`}
              >
                <Icon size={13} /> {n.label}
              </button>
            );
          })}
          <button onClick={doLogout} className="ml-auto px-3 py-2.5 text-red-400/80"><LogOut size={15} /></button>
        </div>

        <div className="px-5 md:px-9 py-8 max-w-[1200px]" data-testid="admin-content">
          {/* ============ DASHBOARD ============ */}
          {view === "dashboard" && stats && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Dashboard</h1>
                  <p className="mt-1.5 text-sm text-white/40">Business ka poora overview — ek nazar mein.</p>
                </div>
                <ActionBtn onClick={load} testid="admin-dash-refresh"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh</ActionBtn>
              </div>

              <div className="mt-7 grid grid-cols-2 xl:grid-cols-4 gap-4">
                <StatCard icon={Package} label="Total Products" value={stats.products_total} testid="stat-total-products" />
                <StatCard icon={ShoppingBag} label="Total Orders" value={stats.product_orders_total} testid="stat-total-orders" />
                <StatCard icon={IndianRupee} label="Total Revenue" value={inr(stats.product_revenue_paise)} gold testid="stat-total-revenue" />
                <StatCard icon={Users} label="Total Subscribers" value={stats.leads_total} testid="stat-total-subscribers" />
              </div>

              {/* Quick actions */}
              <div className="mt-7 flex flex-wrap gap-3" data-testid="quick-actions">
                <ActionBtn gold onClick={() => openProductModal()} testid="qa-add-product"><Plus size={14} /> Add New Product</ActionBtn>
                <ActionBtn onClick={() => setView("orders")} testid="qa-view-orders"><ShoppingBag size={14} /> View All Orders</ActionBtn>
                <ActionBtn onClick={exportCsv} testid="qa-export"><FileDown size={14} /> Export Data</ActionBtn>
              </div>

              {/* Sales analytics — revenue trend + early bird progress */}
              {analytics && (
                <div className="mt-7 grid lg:grid-cols-5 gap-5" data-testid="sales-analytics">
                  <div className="lg:col-span-3">
                    <Card title="Revenue Trend" sub="Last 30 days · paid product orders" testid="admin-revenue-chart-card">
                      <div className="px-4 py-5 h-[240px]" data-testid="admin-revenue-chart">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={analytics.daily} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                            <defs>
                              <linearGradient id="revGold" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#C9A227" stopOpacity={0.45} />
                                <stop offset="100%" stopColor="#C9A227" stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                            <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} axisLine={false} tickLine={false} interval={4} />
                            <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
                            <Tooltip
                              contentStyle={{ background: "#141414", border: "1px solid rgba(201,162,39,0.35)", borderRadius: 12, fontSize: 12 }}
                              labelStyle={{ color: "rgba(255,255,255,0.55)" }}
                              formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Revenue"]}
                              labelFormatter={(d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                            />
                            <Area type="monotone" dataKey="revenue" stroke="#C9A227" strokeWidth={2.5} fill="url(#revGold)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  </div>
                  <div className="lg:col-span-2">
                    <Card title="Early Bird Progress" sub="Live offer tracking — real-time sales" testid="admin-earlybird-card">
                      <div className="px-6 py-6 space-y-6">
                        {analytics.early_bird.length === 0 ? (
                          <p className="py-8 text-center font-mono text-[11px] tracking-[0.2em] uppercase text-white/30">Koi early bird offer active nahi</p>
                        ) : (
                          analytics.early_bird.map((eb) => (
                            <div key={eb.id} data-testid={`admin-eb-${eb.id}`}>
                              <div className="flex items-center justify-between gap-3">
                                <p className="font-medium text-white/90 text-sm truncate">{eb.name}</p>
                                <p className="font-mono text-[10px] font-bold text-[#C9A227] shrink-0" data-testid={`admin-eb-count-${eb.id}`}>{eb.sold}/{eb.limit} SOLD</p>
                              </div>
                              <div className="mt-2.5 h-2.5 rounded-full bg-white/10 overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min(100, (eb.sold / eb.limit) * 100)}%` }}
                                  transition={{ duration: 1 }}
                                  className="h-full rounded-full bg-gradient-to-r from-[#C9A227] to-[#FF7A1A]"
                                />
                              </div>
                              <div className="mt-2 flex items-center justify-between font-mono text-[10px]">
                                <span className="text-white/40">{eb.remaining} spots left @ ₹{eb.early_price.toLocaleString("en-IN")}</span>
                                <span className="text-emerald-400">₹{eb.revenue.toLocaleString("en-IN")} earned</span>
                              </div>
                            </div>
                          ))
                        )}
                        <div className="pt-4 border-t border-white/10 flex items-center justify-between font-mono text-[10px] text-white/40">
                          <span>Abandoned carts: <span className="text-amber-400">{analytics.abandoned_total}</span></span>
                          <span>Reminders sent: <span className="text-white/70">{analytics.reminders_sent}</span></span>
                        </div>
                      </div>
                    </Card>
                  </div>
                </div>
              )}

              {/* Download analytics */}
              {dlAnalytics && (
                <div className="mt-7">
                  <Card
                    title="Download Analytics"
                    sub={`${dlAnalytics.total_downloads} total downloads · product-wise breakdown`}
                    testid="download-analytics-card"
                  >
                    {dlAnalytics.products.length === 0 ? (
                      <p className="px-6 py-12 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Abhi koi download nahi hua</p>
                    ) : (
                      <div className="grid lg:grid-cols-2">
                        <div className="overflow-x-auto border-b lg:border-b-0 lg:border-r border-white/10">
                          <table className="w-full" data-testid="dl-products-table">
                            <thead className="border-b border-white/10">
                              <tr><Th>Product</Th><Th right>Downloads</Th><Th right>Customers</Th><Th right>Links Issued</Th></tr>
                            </thead>
                            <tbody>
                              {dlAnalytics.products.map((p) => (
                                <tr key={p.product_id} className="border-b border-white/5 last:border-0" data-testid={`dl-row-${p.product_id}`}>
                                  <Td><p className="font-medium text-white/90 truncate max-w-[180px]">{p.name}</p></Td>
                                  <Td right mono><span className="text-emerald-400 font-bold">{p.downloads}</span></Td>
                                  <Td right mono>{p.unique_customers}</Td>
                                  <Td right mono><span className="text-white/40">{p.links_issued}</span></Td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="max-h-[280px] overflow-y-auto">
                          <p className="px-6 pt-5 pb-2 font-mono text-[9px] tracking-[0.25em] uppercase text-white/35">Recent Downloads</p>
                          <div className="divide-y divide-white/5">
                            {dlAnalytics.recent.map((r, i) => (
                              <div key={i} className="flex items-center gap-4 px-6 py-3.5" data-testid={`dl-recent-${i}`}>
                                <Download size={14} className="text-[#C9A227] shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-white/90 truncate">{r.customer || r.email}</p>
                                  <p className="text-xs text-white/40 truncate">{r.product_name} · {r.email}</p>
                                </div>
                                <p className="font-mono text-[10px] text-white/40 shrink-0">{r.downloaded_at ? dt(r.downloaded_at) : ""}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {/* Recent orders */}
              <div className="mt-7">
                <Card title="Recent Orders" sub="Latest product purchases" testid="recent-orders-card"
                  action={<button onClick={() => setView("orders")} className="font-mono text-[10px] font-bold tracking-[0.15em] uppercase text-[#C9A227] hover:text-[#E0B82E] transition-colors flex items-center gap-1.5">View All <ExternalLink size={11} /></button>}
                >
                  {sales.length === 0 ? (
                    <p className="px-6 py-14 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Abhi koi order nahi</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="recent-orders-table">
                        <thead className="border-b border-white/10">
                          <tr><Th>Order ID</Th><Th>Customer</Th><Th>Product</Th><Th right>Amount</Th><Th>Date</Th><Th>Status</Th></tr>
                        </thead>
                        <tbody>
                          {sales.slice(0, 6).map((s) => (
                            <tr key={s.purchase_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`recent-order-${s.purchase_id}`}>
                              <Td mono>{s.purchase_id}</Td>
                              <Td><p className="font-medium text-white/90">{s.name}</p><p className="text-xs text-white/40">{s.email}</p></Td>
                              <Td>{s.product_name}</Td>
                              <Td right mono><span className="text-[#C9A227] font-bold">{inr(s.amount)}</span></Td>
                              <Td mono>{dt(s.created_at)}</Td>
                              <Td><Pill s={s.status} /></Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}

          {/* ============ PRODUCTS ============ */}
          {view === "products" && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Products</h1>
                  <p className="mt-1.5 text-sm text-white/40">Digital products manage karo — files upload karo toh auto-delivery live.</p>
                </div>
                <ActionBtn gold onClick={() => openProductModal()} testid="products-add-btn"><Plus size={14} /> Add New Product</ActionBtn>
              </div>

              <div className="mt-7">
                <Card title="Products Manager" sub={`${catalog.length} products · edit, delete, status, files — sab yahin`} testid="admin-products-card">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[900px]" data-testid="products-table">
                      <thead className="border-b border-white/10">
                        <tr>
                          <Th>Product</Th><Th>Category</Th><Th right>Price</Th><Th right>Sold</Th><Th>Stock</Th><Th>Delivery</Th><Th>Status</Th><Th>Visible</Th><Th right>Actions</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.map((p) => {
                          const f = productFiles.find((x) => x.product_id === p.id);
                          return (
                            <tr key={p.id} className={`border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors ${!p.active ? "opacity-45" : ""}`} data-testid={`admin-product-${p.id}`}>
                              <Td>
                                <div className="flex items-center gap-3">
                                  <label className="relative w-11 h-11 rounded-xl bg-white/5 border border-white/10 overflow-hidden shrink-0 cursor-pointer group/t" title="Thumbnail upload" data-testid={`admin-thumb-${p.id}`}>
                                    {p.thumbnail_url ? (
                                      <img src={p.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="absolute inset-0 flex items-center justify-center">{thumbFor === p.id ? <Loader2 size={14} className="animate-spin text-[#C9A227]" /> : <Upload size={14} className="text-white/40 group-hover/t:text-[#C9A227] transition-colors" />}</span>
                                    )}
                                    <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => { uploadThumb("products", p.id, e.target.files[0]); e.target.value = ""; }} />
                                  </label>
                                  <div className="min-w-0">
                                    <p className="font-medium text-white/90 truncate max-w-[200px]">{p.name}</p>
                                    <p className="font-mono text-[10px] text-white/35 truncate max-w-[200px]">{p.description || "—"}</p>
                                  </div>
                                </div>
                              </Td>
                              <Td><span className={`font-mono text-[9px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 rounded-full border ${p.category === "physical" ? "bg-sky-500/10 border-sky-500/30 text-sky-400" : "bg-violet-500/10 border-violet-500/30 text-violet-400"}`}>{p.category || "digital"}</span></Td>
                              <Td right mono>
                                <span className="text-[#C9A227] font-bold">₹{p.price}</span>
                                {p.early_bird && <span className="block text-[9px] text-white/40">early ₹{p.early_bird.early_price} / first {p.early_bird.limit}</span>}
                              </Td>
                              <Td right mono>
                                <span className={p.sold > 0 ? "text-emerald-400 font-bold" : "text-white/40"} data-testid={`admin-sold-${p.id}`}>{p.sold || 0}</span>
                                {p.early_bird && <span className="block text-[9px] text-white/40">{p.early_bird.remaining} early left</span>}
                              </Td>
                              <Td mono>{p.category === "physical" ? (p.stock ?? 0) : "—"}</Td>
                              <Td>
                                {p.category === "physical" ? (
                                  <span className="font-mono text-[10px] text-white/40">WhatsApp order</span>
                                ) : f ? (
                                  <span className="font-mono text-[10px] text-emerald-400 flex items-center gap-1.5"><BadgeCheck size={11} /> {f.original_filename}</span>
                                ) : p.download_link ? (
                                  <span className="font-mono text-[10px] text-emerald-400 flex items-center gap-1.5"><BadgeCheck size={11} /> ext. link</span>
                                ) : (
                                  <label className="font-mono text-[10px] text-amber-400/80 cursor-pointer hover:text-amber-300 flex items-center gap-1.5" data-testid={`admin-upload-${p.id}`}>
                                    {thumbFor === p.id ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} upload file
                                    <input type="file" className="hidden" accept=".zip,.pdf,.cube,.xmp,.dng,.png,.jpg,.jpeg,.mp4,.txt" onChange={(e) => { uploadProductFile(p.id, e.target.files[0]); e.target.value = ""; }} />
                                  </label>
                                )}
                              </Td>
                              <Td>
                                <button
                                  onClick={() => toggleProductStatus(p.id, p.status)}
                                  data-testid={`admin-status-${p.id}`}
                                  className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-bold tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border transition-colors ${p.status === "live" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-[#C9A227]/10 border-[#C9A227]/40 text-[#C9A227]"}`}
                                >
                                  {p.status === "live" ? <BadgeCheck size={11} /> : <Clock3 size={11} />}
                                  {p.status === "live" ? "Live" : "Coming Soon"}
                                </button>
                              </Td>
                              <Td>
                                <button
                                  onClick={() => toggleActive(p)}
                                  data-testid={`admin-active-${p.id}`}
                                  className={`w-9 h-9 rounded-xl border flex items-center justify-center transition-colors ${p.active ? "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10" : "border-white/15 text-white/35 hover:bg-white/5"}`}
                                  title={p.active ? "Hide from store" : "Show on store"}
                                >
                                  {p.active ? <Eye size={14} /> : <EyeOff size={14} />}
                                </button>
                              </Td>
                              <Td right>
                                <div className="flex items-center justify-end gap-2">
                                  <button onClick={() => openProductModal(p)} data-testid={`admin-edit-${p.id}`} className="w-9 h-9 rounded-xl border border-white/15 text-white/60 hover:text-[#C9A227] hover:border-[#C9A227]/40 flex items-center justify-center transition-colors" title="Edit">
                                    <Pencil size={14} />
                                  </button>
                                  <button onClick={() => setDeleteTarget(p)} data-testid={`admin-delete-${p.id}`} className="w-9 h-9 rounded-xl border border-red-500/25 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors" title="Delete">
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </Td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>

              <div className="mt-6">
                <Card title="Stock Shots" sub="City packs abhi 'Coming Soon' hain — launch par yahan manage honge" testid="admin-stock-card">
                  <div className="px-6 py-6 flex flex-wrap gap-2.5">
                    {["Lucknow", "Kanpur", "Prayagraj", "Varanasi", "Jaipur", "Kashmir"].map((c) => (
                      <span key={c} className="font-mono text-[11px] tracking-[0.15em] uppercase bg-white/5 border border-white/10 text-white/60 px-4 py-2.5 rounded-xl">
                        {c} · <span className="text-amber-400/80">Coming Soon</span>
                      </span>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* ============ ORDERS ============ */}
          {view === "orders" && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Orders</h1>
                  <p className="mt-1.5 text-sm text-white/40">Product sales, workshop bookings, consults aur enquiries.</p>
                </div>
                <ActionBtn onClick={exportCsv} testid="orders-export"><FileDown size={13} /> Export CSV</ActionBtn>
              </div>

              <div className="mt-7">
                <Card
                  title="Abandoned Cart Recovery"
                  sub="Payment par ruke buyers — automatic reminder email har ghante jaati hai"
                  testid="abandoned-recovery-card"
                  action={
                    <button
                      onClick={sendAbandonedReminders}
                      disabled={reminding}
                      data-testid="abandoned-remind-btn"
                      className="inline-flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.15em] uppercase text-[#C9A227] hover:text-[#E0B82E] transition-colors disabled:opacity-50"
                    >
                      {reminding ? <Loader2 size={12} className="animate-spin" /> : <MailPlus size={12} />} Send Reminders Now
                    </button>
                  }
                >
                  <div className="px-6 py-5 flex flex-wrap items-center gap-x-10 gap-y-4">
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Abandoned Carts</p>
                      <p className="mt-1 font-display font-black text-3xl text-amber-400" data-testid="abandoned-count">{analytics ? analytics.abandoned_total : sales.filter((s) => s.status === "created").length}</p>
                    </div>
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Reminders Sent</p>
                      <p className="mt-1 font-display font-black text-3xl text-white/85" data-testid="reminders-sent-count">{analytics?.reminders_sent ?? 0}</p>
                    </div>
                    <p className="flex-1 min-w-[220px] text-xs text-white/40 leading-relaxed">
                      Jo buyer order create karke payment complete nahi karta, use 1 ghante baad branded reminder email jaati hai — early bird urgency ke saath. Har buyer ko sirf ek baar.
                    </p>
                  </div>
                </Card>
              </div>

              <div className="mt-6">
                <Card title="Product Orders" sub={`${sales.filter((s) => s.status === "paid").length} paid · ${inr(sales.filter((s) => s.status === "paid").reduce((a, s) => a + (s.amount || 0), 0))} revenue`} testid="orders-products-card">
                  {sales.length === 0 ? (
                    <p className="px-6 py-12 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Abhi koi sale nahi</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="border-b border-white/10">
                          <tr><Th>Order ID</Th><Th>Customer</Th><Th>Product</Th><Th right>Amount</Th><Th>Date</Th><Th>Status</Th><Th right>Download</Th></tr>
                        </thead>
                        <tbody>
                          {sales.map((s) => (
                            <tr key={s.purchase_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]" data-testid={`order-row-${s.purchase_id}`}>
                              <Td mono>{s.purchase_id}</Td>
                              <Td><p className="font-medium text-white/90">{s.name}</p><p className="text-xs text-white/40">{s.email}{s.phone ? ` · ${s.phone}` : ""}</p></Td>
                              <Td>{s.product_name}</Td>
                              <Td right mono><span className="text-[#C9A227] font-bold">{inr(s.amount)}</span></Td>
                              <Td mono>{dt(s.created_at)}</Td>
                              <Td><Pill s={s.status} /></Td>
                              <Td right>
                                {s.status === "paid" && productFiles.some((f) => f.product_id === s.product_id) ? (
                                  <button
                                    onClick={() => resendLink(s.purchase_id)}
                                    disabled={resendingFor === s.purchase_id}
                                    data-testid={`resend-${s.purchase_id}`}
                                    className="inline-flex items-center gap-1.5 font-mono text-[9px] font-bold tracking-[0.12em] uppercase px-3 py-2 rounded-full border border-[#C9A227]/40 text-[#C9A227] hover:bg-[#C9A227] hover:text-[#0a0a0a] transition-colors disabled:opacity-50"
                                    title="Naya one-time link generate karke customer ko email karo"
                                  >
                                    {resendingFor === s.purchase_id ? <Loader2 size={11} className="animate-spin" /> : <MailPlus size={11} />} Resend Link
                                  </button>
                                ) : (
                                  <span className="font-mono text-[10px] text-white/25">—</span>
                                )}
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>

              <div className="mt-6 grid xl:grid-cols-2 gap-6">
                <Card title="Course Enrollments" sub={`${courseSales.filter((c) => c.status === "paid").length} enrolled`} testid="orders-courses-card">
                  <div className="divide-y divide-white/5">
                    {courseSales.length === 0 && <p className="px-6 py-10 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Koi enrollment nahi</p>}
                    {courseSales.map((c) => (
                      <div key={c.purchase_id} className="flex items-center gap-4 px-6 py-4" data-testid={`course-order-row-${c.purchase_id}`}>
                        <GraduationCap size={15} className="text-[#C9A227] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white/90 truncate">{c.name} <span className="text-white/35 font-mono text-[10px]">{c.purchase_id}</span></p>
                          <p className="text-xs text-white/40 font-mono truncate">{c.course_name} · {c.amount === 0 ? "Free" : inr(c.amount)} · {c.email}</p>
                        </div>
                        <Pill s={c.status} />
                      </div>
                    ))}
                  </div>
                </Card>
                <Card title="Workshop Bookings" sub={`${workshopSales.filter((w) => w.status === "paid").length} paid seats`} testid="orders-workshop-card">
                  <div className="divide-y divide-white/5">
                    {workshopSales.length === 0 && <p className="px-6 py-10 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Koi booking nahi</p>}
                    {workshopSales.map((w) => (
                      <div key={w.booking_id} className="flex items-center gap-4 px-6 py-4" data-testid={`workshop-row-${w.booking_id}`}>
                        <Video size={15} className="text-[#C9A227] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white/90 truncate">{w.name} <span className="text-white/35 font-mono text-[10px]">{w.booking_id}</span></p>
                          <p className="text-xs text-white/40 font-mono">{w.batch} · {w.plan} · {inr(w.amount)}</p>
                        </div>
                        <Pill s={w.status} />
                      </div>
                    ))}
                  </div>
                </Card>

                <Card title="Consult Bookings" sub={`${bookings.length} total`} testid="orders-consult-card">
                  <div className="divide-y divide-white/5">
                    {bookings.length === 0 && <p className="px-6 py-10 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Koi consult nahi</p>}
                    {bookings.slice(0, 8).map((b) => (
                      <div key={b.booking_id} className="flex items-center gap-4 px-6 py-4" data-testid={`consult-row-${b.booking_id}`}>
                        <CalendarCheck size={15} className="text-[#C9A227] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white/90 truncate">{b.phone} <span className="text-white/35 font-mono text-[10px]">{b.booking_id}</span></p>
                          <p className="text-xs text-white/40 font-mono">{b.topic} · {b.preferred_datetime?.replace("T", " ")}</p>
                        </div>
                        <Pill s={b.status} />
                        {b.status === "paid" && (
                          <button onClick={() => updateStatus("bookings", b.booking_id, "completed")} className="font-mono text-[9px] font-bold tracking-[0.12em] uppercase text-emerald-400/80 hover:text-emerald-400 transition-colors">Done</button>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              <div className="mt-6">
                <Card title="Shoot Enquiries" sub={`${enquiries.filter((e) => e.status === "new" || !e.status).length} new`} testid="orders-enquiries-card">
                  <div className="divide-y divide-white/5">
                    {enquiries.length === 0 && <p className="px-6 py-10 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Koi enquiry nahi</p>}
                    {enquiries.map((e) => (
                      <div key={e.enquiry_id} className="flex flex-wrap items-center gap-4 px-6 py-4" data-testid={`enquiry-row-${e.enquiry_id}`}>
                        <MessageSquare size={15} className="text-[#C9A227] shrink-0" />
                        <div className="flex-1 min-w-[180px]">
                          <p className="text-sm font-medium text-white/90">{e.name} <span className="text-white/35 font-mono text-[10px]">{e.whatsapp}</span></p>
                          <p className="text-xs text-white/40">{e.projectType}{e.budget ? ` · ${e.budget}` : ""}{e.date ? ` · ${e.date}` : ""}</p>
                        </div>
                        <Pill s={e.status || "new"} />
                        <div className="flex gap-2">
                          {(!e.status || e.status === "new") && (
                            <button onClick={() => updateStatus("enquiries", e.enquiry_id, "contacted")} className="font-mono text-[9px] font-bold tracking-[0.12em] uppercase text-[#C9A227] hover:text-[#E0B82E] transition-colors">Contacted</button>
                          )}
                          {e.status === "contacted" && (
                            <button onClick={() => updateStatus("enquiries", e.enquiry_id, "closed")} className="font-mono text-[9px] font-bold tracking-[0.12em] uppercase text-white/50 hover:text-white transition-colors">Close</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* ============ COURSES ============ */}
          {view === "courses" && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Courses</h1>
                  <p className="mt-1.5 text-sm text-white/40">Film School ke courses manage karo — status ek click mein.</p>
                </div>
                <ActionBtn gold onClick={() => openCourseModal()} testid="courses-add-btn"><Plus size={14} /> Add New Course</ActionBtn>
              </div>

              <div className="mt-7">
                <Card title="Courses Manager" sub={`${courses.length} courses`} testid="admin-courses-card">
                  {courses.length === 0 ? (
                    <p className="px-6 py-14 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Abhi koi course nahi — Add New Course se shuru karo</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[900px]" data-testid="courses-table">
                        <thead className="border-b border-white/10">
                          <tr><Th>Course</Th><Th right>Price</Th><Th right>Seats</Th><Th>Launch</Th><Th>Enrollment</Th><Th>Status (click to cycle)</Th><Th right>Actions</Th></tr>
                        </thead>
                        <tbody>
                          {courses.map((c) => (
                            <tr key={c.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`admin-course-${c.id}`}>
                              <Td>
                                <div className="flex items-center gap-3">
                                  <label className="relative w-11 h-11 rounded-xl bg-white/5 border border-white/10 overflow-hidden shrink-0 cursor-pointer group/t" title="Thumbnail upload" data-testid={`admin-course-thumb-${c.id}`}>
                                    {c.thumbnail_url ? (
                                      <img src={c.thumbnail_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                      <span className="absolute inset-0 flex items-center justify-center">{thumbFor === c.id ? <Loader2 size={14} className="animate-spin text-[#C9A227]" /> : <Upload size={14} className="text-white/40 group-hover/t:text-[#C9A227] transition-colors" />}</span>
                                    )}
                                    <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => { uploadThumb("courses", c.id, e.target.files[0]); e.target.value = ""; }} />
                                  </label>
                                  <div className="min-w-0">
                                    <p className="font-medium text-white/90 truncate max-w-[200px]">{c.name}</p>
                                    <p className="font-mono text-[10px] text-white/35 truncate max-w-[200px]">{c.course_type ? `${c.course_type} · ` : ""}{c.description || "—"}</p>
                                  </div>
                                </div>
                              </Td>
                              <Td right mono><span className="text-[#C9A227] font-bold">{c.price ? `₹${c.price}` : "Free"}</span></Td>
                              <Td right mono>
                                {c.seat_limit ? (
                                  <span data-testid={`admin-course-seats-${c.id}`}>
                                    <span className="text-white/85 font-bold">{c.seats_taken}</span>
                                    <span className="text-white/35">/{c.seat_limit}</span>
                                    <span className={`block text-[9px] ${c.seats_remaining === 0 ? "text-red-400" : "text-emerald-400"}`}>
                                      {c.seats_remaining === 0 ? "FULL" : `${c.seats_remaining} left`}
                                    </span>
                                  </span>
                                ) : (
                                  <span className="text-white/30" data-testid={`admin-course-seats-${c.id}`}>{c.seats_taken} / ∞</span>
                                )}
                              </Td>
                              <Td mono>{c.launch_date || "—"}</Td>
                              <Td>
                                {c.enrollment_link ? (
                                  <a href={c.enrollment_link} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-sky-400 hover:text-sky-300 flex items-center gap-1.5"><ExternalLink size={11} /> link set</a>
                                ) : (
                                  <span className="font-mono text-[10px] text-white/35">—</span>
                                )}
                              </Td>
                              <Td>
                                <button
                                  onClick={() => toggleCourseStatus(c.id, c.status)}
                                  data-testid={`admin-course-status-${c.id}`}
                                  className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-bold tracking-[0.12em] uppercase px-3 py-1.5 rounded-full border transition-colors ${
                                    c.status === "active" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400"
                                    : c.status === "coming_soon" ? "bg-[#C9A227]/10 border-[#C9A227]/40 text-[#C9A227]"
                                    : "bg-red-500/10 border-red-500/30 text-red-400"
                                  }`}
                                >
                                  {c.status === "active" ? <BadgeCheck size={11} /> : <Clock3 size={11} />}
                                  {c.status.replace("_", " ")}
                                </button>
                              </Td>
                              <Td right>
                                <div className="flex items-center justify-end gap-2">
                                  <button onClick={() => openEnrollments(c.id)} data-testid={`admin-course-students-${c.id}`} className="w-9 h-9 rounded-xl border border-white/15 text-white/60 hover:text-[#C9A227] hover:border-[#C9A227]/40 flex items-center justify-center transition-colors" title="Enrollments / Students">
                                    <Users size={14} />
                                  </button>
                                  <button onClick={() => setResetCourseTarget(c)} data-testid={`admin-course-reset-${c.id}`} className="w-9 h-9 rounded-xl border border-amber-500/25 text-amber-400/70 hover:text-amber-400 hover:bg-amber-500/10 flex items-center justify-center transition-colors" title="Reset Reservations">
                                    <RotateCcw size={14} />
                                  </button>
                                  <button onClick={() => openCourseModal(c)} data-testid={`admin-course-edit-${c.id}`} className="w-9 h-9 rounded-xl border border-white/15 text-white/60 hover:text-[#C9A227] hover:border-[#C9A227]/40 flex items-center justify-center transition-colors" title="Edit">
                                    <Pencil size={14} />
                                  </button>
                                  <button onClick={() => deleteCourse(c.id, c.name)} data-testid={`admin-course-delete-${c.id}`} className="w-9 h-9 rounded-xl border border-red-500/25 text-red-400/70 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors" title="Delete">
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}

          {/* ============ CONSULTATIONS ============ */}
          {view === "consultations" && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Consultations</h1>
                  <p className="mt-1.5 text-sm text-white/40">Services page ke free consultation requests — confirm karke call karo.</p>
                </div>
                <span className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#C9A227] bg-[#C9A227]/10 border border-[#C9A227]/25 rounded-full px-4 py-2" data-testid="consult-pending-count">
                  {consultations.filter((c) => c.status === "pending").length} pending
                </span>
              </div>

              <div className="mt-7">
                <Card title="Consultation Requests" sub={`${consultations.length} total`} testid="consultations-card">
                  {consultations.length === 0 ? (
                    <p className="px-6 py-14 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Abhi koi consultation request nahi</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[900px]" data-testid="consultations-table">
                        <thead className="border-b border-white/10">
                          <tr><Th>ID</Th><Th>Customer</Th><Th>Service</Th><Th>Preferred</Th><Th>Brief</Th><Th>Status</Th></tr>
                        </thead>
                        <tbody>
                          {consultations.map((c) => (
                            <tr key={c.consult_id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`consult-row-${c.consult_id}`}>
                              <Td mono>{c.consult_id}</Td>
                              <Td>
                                <p className="font-medium text-white/90">{c.name}</p>
                                <p className="text-xs text-white/40 font-mono">{c.phone} · {c.email}</p>
                              </Td>
                              <Td><span className="font-mono text-[10px] font-bold tracking-[0.1em] uppercase px-3 py-1.5 rounded-full border bg-sky-500/10 border-sky-500/30 text-sky-400">{c.service_type}</span></Td>
                              <Td mono>{(c.preferred_datetime || "").replace("T", " · ")}</Td>
                              <Td><p className="text-xs text-white/50 max-w-[220px] truncate" title={c.brief}>{c.brief || "—"}</p></Td>
                              <Td>
                                <select
                                  value={c.status}
                                  onChange={(e) => setConsultStatus(c.consult_id, e.target.value)}
                                  data-testid={`consult-status-${c.consult_id}`}
                                  className={`font-mono text-[10px] font-bold tracking-[0.12em] uppercase px-3 py-2 rounded-full border outline-none cursor-pointer bg-[#0a0a0a] ${
                                    c.status === "confirmed" ? "border-emerald-500/40 text-emerald-400"
                                    : c.status === "completed" ? "border-white/25 text-white/50"
                                    : c.status === "cancelled" ? "border-red-500/40 text-red-400"
                                    : "border-[#C9A227]/40 text-[#C9A227]"
                                  }`}
                                >
                                  <option value="pending">Pending</option>
                                  <option value="confirmed">Confirmed</option>
                                  <option value="completed">Completed</option>
                                  <option value="cancelled">Cancelled</option>
                                </select>
                              </Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
              </div>
            </>
          )}

          {/* ============ NOTIFICATIONS ============ */}
          {view === "notifications" && (
            <>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Notifications</h1>
                  <p className="mt-1.5 text-sm text-white/40">Email subscribers jo "Get Notified" par click karte hain + email blast.</p>
                </div>
              </div>

              <div className="mt-7">
                <Card title="Email Blast" sub="Selected list ke sabhi subscribers ko announcement bhejo" testid="blast-card"
                  action={<span className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#C9A227] bg-[#C9A227]/10 border border-[#C9A227]/25 rounded-full px-4 py-2" data-testid="blast-count">{blastCount} recipients</span>}
                >
                  <div className="p-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <select data-testid="blast-resource" value={blastRes} onChange={(e) => setBlastRes(e.target.value)} className={`${inputDark} cursor-pointer`}>
                        {leadResources.map((r) => (
                          <option key={r} value={r} className="bg-[#111]">{r === "all" ? "Sabhi subscribers" : r}</option>
                        ))}
                      </select>
                      <input data-testid="blast-subject" value={blastSubject} onChange={(e) => setBlastSubject(e.target.value)} placeholder="Subject — e.g. DJI Cinematic Field Guide is LIVE!" className={inputDark} />
                    </div>
                    <textarea data-testid="blast-message" value={blastMessage} onChange={(e) => setBlastMessage(e.target.value)} rows={4} placeholder="Message likho — launch details, link, offer…" className={`${inputDark} resize-y`} />
                    <ActionBtn gold onClick={sendBlast} disabled={blastSending || blastCount === 0} testid="blast-send">
                      {blastSending ? <Loader2 size={13} className="animate-spin" /> : <MailPlus size={13} />} {blastSending ? "Bhej rahe…" : "Send Blast"}
                    </ActionBtn>
                  </div>
                </Card>
              </div>

              <div className="mt-6">
                <Card title="Subscribers" sub={`${leads.length} total`} testid="subscribers-card">
                  <div className="divide-y divide-white/5">
                    {leads.length === 0 && <p className="px-6 py-10 text-center font-mono text-[11px] tracking-[0.25em] uppercase text-white/30">Koi subscriber nahi</p>}
                    {leads.map((l) => (
                      <div key={l.lead_id} className="flex flex-wrap items-center gap-4 px-6 py-4" data-testid={`subscriber-row-${l.lead_id}`}>
                        <Bell size={15} className="text-[#C9A227] shrink-0" />
                        <div className="flex-1 min-w-[180px]">
                          <p className="text-sm font-medium text-white/90">{l.name}</p>
                          <p className="text-xs text-white/40 font-mono">{l.email} · {l.resource}</p>
                        </div>
                        <span className="font-mono text-[11px] text-white/35">{dt(l.created_at)}</span>
                        <Pill s={l.status || "new"} />
                        <div className="flex gap-2">
                          {(!l.status || l.status === "new") && (
                            <button onClick={() => updateStatus("leads", l.lead_id, "contacted")} className="font-mono text-[9px] font-bold tracking-[0.12em] uppercase text-[#C9A227] hover:text-[#E0B82E] transition-colors">Contacted</button>
                          )}
                          {l.status === "contacted" && (
                            <button onClick={() => updateStatus("leads", l.lead_id, "closed")} className="font-mono text-[9px] font-bold tracking-[0.12em] uppercase text-white/50 hover:text-white transition-colors">Close</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* ============ SETTINGS ============ */}
          {view === "settings" && (
            <>
              <div>
                <h1 className="font-display font-black text-3xl text-white" data-testid="admin-view-title">Settings</h1>
                <p className="mt-1.5 text-sm text-white/40">Configuration status aur quick links.</p>
              </div>
              <div className="mt-7 grid md:grid-cols-2 gap-6">
                <Card title="System Status" testid="settings-status-card">
                  <div className="divide-y divide-white/5">
                    {[
                      { k: "Payments (Razorpay)", v: "Live", ok: true },
                      { k: "Owner Email", v: "screenextfilms@gmail.com", ok: true },
                      { k: "Auto Delivery (Storage)", v: "Connected", ok: true },
                      { k: "Email Service", v: "Active", ok: true },
                      { k: "Admin Token", v: "akki•••••2026", ok: true },
                    ].map((r) => (
                      <div key={r.k} className="flex items-center justify-between px-6 py-4">
                        <span className="text-sm text-white/60">{r.k}</span>
                        <span className="font-mono text-[11px] font-bold tracking-[0.1em] uppercase text-emerald-400 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-emerald-400" /> {r.v}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card title="Quick Links" testid="settings-links-card">
                  <div className="p-6 grid gap-3">
                    {[
                      { t: "Store (Products)", u: "/products" },
                      { t: "Workshop Booking", u: "/book-workshop" },
                      { t: "My Orders (customer)", u: "/my-orders" },
                      { t: "Film School", u: "/film-school" },
                      { t: "Homepage", u: "/" },
                    ].map((l) => (
                      <a key={l.u} href={l.u} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-5 py-4 hover:border-[#C9A227]/40 hover:bg-[#C9A227]/5 transition-all group">
                        <span className="text-sm text-white/80 group-hover:text-white transition-colors">{l.t}</span>
                        <ExternalLink size={14} className="text-white/30 group-hover:text-[#C9A227] transition-colors" />
                      </a>
                    ))}
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Course enrollments modal */}
      <AnimatePresence>
        {enrollFor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-5 py-8 overflow-y-auto"
            onClick={() => setEnrollFor(null)}
            data-testid="course-enroll-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl bg-[#111111] border border-white/15 rounded-2xl p-8 my-auto"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9A227]">Enrollments</p>
                  <h3 className="mt-2 font-display font-black text-2xl text-white">{enrollData?.course?.name || "…"}</h3>
                </div>
                <button onClick={() => setEnrollFor(null)} data-testid="course-enroll-close" className="w-9 h-9 rounded-xl border border-white/15 text-white/50 hover:text-white flex items-center justify-center transition-colors">
                  <X size={15} />
                </button>
              </div>
              {enrollLoading || !enrollData ? (
                <div className="py-14 flex justify-center" data-testid="course-enroll-loading"><Loader2 size={22} className="animate-spin text-[#C9A227]" /></div>
              ) : (
                <>
                  <div className="mt-6 grid grid-cols-3 gap-3.5">
                    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 text-center">
                      <p className="font-display font-black text-2xl text-white" data-testid="enroll-stat-total">{enrollData.total}</p>
                      <p className="mt-1 font-mono text-[9px] tracking-[0.18em] uppercase text-white/40">Total Enrollments</p>
                    </div>
                    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4 text-center">
                      <p className="font-display font-black text-2xl text-emerald-400" data-testid="enroll-stat-paid">{enrollData.paid}</p>
                      <p className="mt-1 font-mono text-[9px] tracking-[0.18em] uppercase text-white/40">Paid</p>
                    </div>
                    <div className="bg-white/[0.04] border border-[#C9A227]/25 rounded-xl p-4 text-center">
                      <p className="font-display font-black text-2xl text-[#C9A227]" data-testid="enroll-stat-revenue">₹{enrollData.revenue.toLocaleString("en-IN")}</p>
                      <p className="mt-1 font-mono text-[9px] tracking-[0.18em] uppercase text-white/40">Revenue</p>
                    </div>
                  </div>
                  <div className="mt-6 max-h-[320px] overflow-y-auto rounded-xl border border-white/10">
                    {enrollData.students.length === 0 ? (
                      <p className="py-12 text-center font-mono text-[10px] tracking-[0.25em] uppercase text-white/30">Abhi koi enrollment nahi</p>
                    ) : (
                      <table className="w-full" data-testid="enroll-students-table">
                        <thead className="border-b border-white/10 sticky top-0 bg-[#161616]">
                          <tr><Th>Student</Th><Th>Detail</Th><Th>Date</Th><Th>Status</Th></tr>
                        </thead>
                        <tbody>
                          {enrollData.students.map((st) => (
                            <tr key={st.id} className="border-b border-white/5 last:border-0" data-testid={`enroll-row-${st.id}`}>
                              <Td><p className="font-medium text-white/90">{st.name}</p><p className="text-xs text-white/40">{st.email}{st.phone ? ` · ${st.phone}` : ""}</p></Td>
                              <Td mono>{st.detail ? `${st.detail} · ` : ""}{st.amount ? inr(st.amount) : "Free"}</Td>
                              <Td mono>{st.date ? dt(st.date) : "—"}</Td>
                              <Td><Pill s={st.status} /></Td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete product confirmation popup */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/75 backdrop-blur-sm flex items-center justify-center px-5"
            onClick={() => !deleting && setDeleteTarget(null)}
            data-testid="product-delete-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm bg-[#111111] border border-red-500/25 rounded-2xl p-8 text-center"
            >
              <span className="mx-auto w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <Trash2 size={22} className="text-red-400" />
              </span>
              <h3 className="mt-5 font-display font-bold text-xl text-white" data-testid="product-delete-title">Are you sure you want to delete this product?</h3>
              <p className="mt-2.5 text-sm text-white/45 leading-relaxed">
                <strong className="text-white/80">{deleteTarget.name}</strong> permanently database se delete ho jayega aur store se turant hat jayega. Yeh action undo nahi hoga.
              </p>
              <div className="mt-7 grid grid-cols-2 gap-3">
                <button
                  data-testid="product-delete-cancel"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="font-mono text-[11px] font-bold tracking-[0.18em] uppercase px-5 py-3.5 rounded-xl border border-white/15 text-white/60 hover:text-white hover:border-white/35 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="product-delete-confirm"
                  onClick={deleteProduct}
                  disabled={deleting}
                  className="inline-flex items-center justify-center gap-2 font-mono text-[11px] font-bold tracking-[0.18em] uppercase px-5 py-3.5 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-60"
                >
                  {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit product modal */}
      <AnimatePresence>
        {addOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-5 py-8 overflow-y-auto"
            onClick={() => setAddOpen(false)}
            data-testid="add-product-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#111111] border border-white/15 rounded-2xl p-8 my-auto"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9A227]">{editProduct ? "Edit Product" : "New Product"}</p>
                  <h3 className="mt-2 font-display font-black text-2xl text-white">{editProduct ? "Update Product" : "Add Product"}</h3>
                </div>
                <button onClick={() => setAddOpen(false)} data-testid="add-product-close" className="w-9 h-9 rounded-xl border border-white/15 text-white/50 hover:text-white flex items-center justify-center transition-colors">
                  <X size={15} />
                </button>
              </div>
              <div className="mt-6 space-y-3.5">
                <input data-testid="add-product-name" value={newProduct.name} onChange={(e) => setNewProduct((p) => ({ ...p, name: e.target.value }))} placeholder="Product Name — e.g. Wedding LUT Pack" className={inputDark} />
                <textarea data-testid="add-product-desc" value={newProduct.description} onChange={(e) => setNewProduct((p) => ({ ...p, description: e.target.value }))} rows={2} placeholder="Description" className={`${inputDark} resize-none`} />
                <div className="grid grid-cols-2 gap-3.5">
                  <input data-testid="add-product-price" value={newProduct.price} onChange={(e) => setNewProduct((p) => ({ ...p, price: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="Price (INR)" className={inputDark} />
                  <select data-testid="add-product-category" value={newProduct.category} onChange={(e) => setNewProduct((p) => ({ ...p, category: e.target.value }))} className={`${inputDark} cursor-pointer`}>
                    <option value="digital" className="bg-[#111]">Digital</option>
                    <option value="physical" className="bg-[#111]">Physical</option>
                  </select>
                </div>
                {newProduct.category === "physical" && (
                  <input data-testid="add-product-stock" value={newProduct.stock} onChange={(e) => setNewProduct((p) => ({ ...p, stock: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="Stock quantity" className={inputDark} />
                )}
                <div className="rounded-xl border border-[#C9A227]/25 bg-[#C9A227]/[0.05] p-4">
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#C9A227]">Early Bird Offer (optional)</p>
                  <div className="mt-3 grid grid-cols-2 gap-3.5">
                    <input data-testid="add-product-eb-limit" value={newProduct.eb_limit} onChange={(e) => setNewProduct((p) => ({ ...p, eb_limit: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="First N buyers (e.g. 100)" className={inputDark} />
                    <input data-testid="add-product-eb-price" value={newProduct.eb_price} onChange={(e) => setNewProduct((p) => ({ ...p, eb_price: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="Early price ₹ (e.g. 499)" className={inputDark} />
                  </div>
                  <p className="mt-2 text-[11px] text-white/35">N sales ke baad price automatically regular ₹ ho jayega. Khali chhodo toh offer off.</p>
                </div>
                {newProduct.category === "digital" && (
                  <input data-testid="add-product-download" value={newProduct.download_link} onChange={(e) => setNewProduct((p) => ({ ...p, download_link: e.target.value }))} placeholder="Download link (optional — Drive/Dropbox URL)" className={inputDark} />
                )}
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2">Thumbnail Image</p>
                  <label data-testid="add-product-thumb-label" className="flex items-center gap-4 cursor-pointer group/thumb">
                    <span className="w-20 h-20 rounded-xl bg-white/5 border border-dashed border-white/20 group-hover/thumb:border-[#C9A227]/60 overflow-hidden flex items-center justify-center shrink-0 transition-colors">
                      {pendingThumbPreview ? (
                        <img src={pendingThumbPreview} alt="Thumbnail preview" className="w-full h-full object-cover" />
                      ) : (
                        <ImagePlus size={20} className="text-white/30 group-hover/thumb:text-[#C9A227] transition-colors" />
                      )}
                    </span>
                    <span className="flex flex-col gap-1 min-w-0">
                      <span className="inline-flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.15em] uppercase text-[#C9A227]">
                        <Upload size={12} /> {pendingThumbPreview ? "Change Image" : "Upload Image"}
                      </span>
                      <span className="text-[11px] text-white/35 truncate max-w-[240px]">{pendingThumb ? pendingThumb.name : "JPG / PNG / WEBP — max 15MB"}</span>
                    </span>
                    <input data-testid="add-product-thumb" type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => { pickThumb(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <select data-testid="add-product-status" value={newProduct.status} onChange={(e) => setNewProduct((p) => ({ ...p, status: e.target.value }))} className={`${inputDark} cursor-pointer`}>
                    <option value="coming_soon" className="bg-[#111]">Coming Soon</option>
                    <option value="live" className="bg-[#111]">Live (Buy Now)</option>
                  </select>
                  <button
                    type="button"
                    data-testid="add-product-active"
                    onClick={() => setNewProduct((p) => ({ ...p, active: !p.active }))}
                    className={`flex items-center justify-center gap-2 font-mono text-[11px] font-bold tracking-[0.15em] uppercase px-4 py-3 rounded-xl border transition-colors ${newProduct.active ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-400" : "bg-white/5 border-white/15 text-white/50"}`}
                  >
                    {newProduct.active ? <Eye size={13} /> : <EyeOff size={13} />} {newProduct.active ? "Active" : "Hidden"}
                  </button>
                </div>
              </div>
              <button
                data-testid="add-product-submit"
                onClick={saveProduct}
                disabled={adding}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] text-[#0a0a0a] font-mono text-[11px] font-bold tracking-[0.2em] uppercase px-6 py-4 rounded-xl hover:bg-[#E0B82E] transition-colors disabled:opacity-60"
              >
                {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {editProduct ? "Save Changes" : "Add to Store"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit course modal */}
      <AnimatePresence>
        {courseOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-5 py-8 overflow-y-auto"
            onClick={() => setCourseOpen(false)}
            data-testid="add-course-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.97 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-[#111111] border border-white/15 rounded-2xl p-8 my-auto"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#C9A227]">{editCourse ? "Edit Course" : "New Course"}</p>
                  <h3 className="mt-2 font-display font-black text-2xl text-white">{editCourse ? "Update Course" : "Add Course"}</h3>
                </div>
                <button onClick={() => setCourseOpen(false)} data-testid="add-course-close" className="w-9 h-9 rounded-xl border border-white/15 text-white/50 hover:text-white flex items-center justify-center transition-colors">
                  <X size={15} />
                </button>
              </div>
              <div className="mt-6 space-y-3.5">
                <input data-testid="add-course-name" value={newCourse.name} onChange={(e) => setNewCourse((p) => ({ ...p, name: e.target.value }))} placeholder="Course Name — e.g. FPV Masterclass Online" className={inputDark} />
                <textarea data-testid="add-course-desc" value={newCourse.description} onChange={(e) => setNewCourse((p) => ({ ...p, description: e.target.value }))} rows={2} placeholder="Description" className={`${inputDark} resize-none`} />
                <div className="grid grid-cols-2 gap-3.5">
                  <input data-testid="add-course-price" value={newCourse.price} onChange={(e) => setNewCourse((p) => ({ ...p, price: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="Price (INR, 0 = Free)" className={inputDark} />
                  <input data-testid="add-course-launch" type="date" value={newCourse.launch_date} onChange={(e) => setNewCourse((p) => ({ ...p, launch_date: e.target.value }))} className={`${inputDark} [color-scheme:dark]`} />
                </div>
                <input data-testid="add-course-type" value={newCourse.course_type} onChange={(e) => setNewCourse((p) => ({ ...p, course_type: e.target.value }))} placeholder="Type — e.g. Masterclass / Workshop / Online Course" className={inputDark} />
                <input data-testid="add-course-seats" value={newCourse.seat_limit} onChange={(e) => setNewCourse((p) => ({ ...p, seat_limit: e.target.value.replace(/[^0-9]/g, "") }))} placeholder="Max seats (optional — khali = unlimited)" className={inputDark} />
                <input data-testid="add-course-link" value={newCourse.enrollment_link} onChange={(e) => setNewCourse((p) => ({ ...p, enrollment_link: e.target.value }))} placeholder="Enrollment link (payment/landing page URL)" className={inputDark} />
                <div>
                  <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2">Thumbnail Image</p>
                  <label data-testid="add-course-thumb-label" className="flex items-center gap-4 cursor-pointer group/cthumb">
                    <span className="w-20 h-20 rounded-xl bg-white/5 border border-dashed border-white/20 group-hover/cthumb:border-[#C9A227]/60 overflow-hidden flex items-center justify-center shrink-0 transition-colors">
                      {pendingThumbPreview ? (
                        <img src={pendingThumbPreview} alt="Thumbnail preview" className="w-full h-full object-cover" />
                      ) : (
                        <ImagePlus size={20} className="text-white/30 group-hover/cthumb:text-[#C9A227] transition-colors" />
                      )}
                    </span>
                    <span className="flex flex-col gap-1 min-w-0">
                      <span className="inline-flex items-center gap-2 font-mono text-[10px] font-bold tracking-[0.15em] uppercase text-[#C9A227]">
                        <Upload size={12} /> {pendingThumbPreview ? "Change Image" : "Upload from Computer"}
                      </span>
                      <span className="text-[11px] text-white/35 truncate max-w-[240px]">{pendingThumb ? pendingThumb.name : "JPG / PNG / WEBP — max 15MB"}</span>
                    </span>
                    <input data-testid="add-course-thumb" type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => { pickThumb(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </div>
                <select data-testid="add-course-status" value={newCourse.status} onChange={(e) => setNewCourse((p) => ({ ...p, status: e.target.value }))} className={`${inputDark} cursor-pointer`}>
                  <option value="coming_soon" className="bg-[#111]">Coming Soon</option>
                  <option value="active" className="bg-[#111]">Active</option>
                  <option value="closed" className="bg-[#111]">Closed</option>
                </select>
              </div>
              <button
                data-testid="add-course-submit"
                onClick={saveCourse}
                disabled={savingCourse}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#C9A227] text-[#0a0a0a] font-mono text-[11px] font-bold tracking-[0.2em] uppercase px-6 py-4 rounded-xl hover:bg-[#E0B82E] transition-colors disabled:opacity-60"
              >
                {savingCourse ? <Loader2 size={14} className="animate-spin" /> : <GraduationCap size={14} />} {editCourse ? "Save Changes" : "Add Course"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
